import { useState } from "react";
import "./ContactUs.css";
import Header from "../components/common/Header";
import Footer from "../components/common/Footer";
import config from "../config"; // src/config.js

// API base URL comes from src/config.js (routes are mounted under /api). Final URL: POST {baseURL}/api/contact-requests
const API_BASE_URL = `${config.baseURL}/api`;

// Sends the form to the backend; throws an Error with a readable message on failure.
async function submitContactRequest(payload) {
  const res = await fetch(`${API_BASE_URL}/contact-requests`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    // Backend returns { message } or { message, errors: { field: "msg" } }
    const firstFieldError = data.errors && Object.values(data.errors)[0];
    throw new Error(data.message || firstFieldError || `Request failed (${res.status})`);
  }
  return data;
}

const STATES = ["Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "District of Columbia", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming", "Outside the US"];

const ROLES = [
  { value: "Expecting/planning mother", group: "member", label: "Expecting / planning mother", small: "Pregnant or trying to conceive" },
  { value: "Surrogate", group: "member", label: "Surrogate", small: "Gestational carrier" },
  { value: "Physician/OB", group: "provider", label: "Physician / OB", small: "Individual clinician" },
  { value: "IVF clinic or agency", group: "provider", label: "IVF clinic or agency", small: "Group pilot or referrals" },
];

const STAGES = [
  "Planning / trying to conceive",
  "In IVF or fertility treatment",
  "Matched (surrogacy), not yet pregnant",
  "First trimester",
  "Second trimester",
  "Third trimester",
  "Postpartum",
];

const SOURCES = [
  "Google search",
  "AI assistant (ChatGPT, Gemini, etc.)",
  "Social media",
  "My doctor or clinic",
  "Surrogacy4All or another agency",
  "Friend or family",
  "Conference or event",
  "Other",
];

const NAV = [
  ["/how-it-works", "How it works"],
  ["/pregnancy", "Pregnancy"],
  ["/surrogacy", "Surrogacy"],
  ["/for-physicians", "For physicians"],
  ["/for-clinics", "For clinics"],
  ["/pricing", "Pricing"],
];

const KEEP = [
  ["/how-it-works", "How It Works →"],
  ["/pregnancy-monitoring", "Pregnancy Monitoring →"],
  ["/pricing", "Pricing →"],
  ["/about", "About & Clinical Oversight →"],
  ["/privacy-safety", "Privacy & Safety →"],
  ["/faq", "FAQ →"],
];

const FAQS = [
  ["How quickly will you respond?", "Most requests receive a reply within one business day.", true],
  ["Can I send medical questions here?", "Please don't send urgent medical questions through this form. Contact your provider or call 911."],
  ["Is the pilot really free?", "Yes. Eligible participants pay no subscription fee through December 31, 2026."],
  ["Who will contact me?", "A member of the FutureFamilyAI team matched to your role."],
  ["Can clinics request a group pilot?", "Yes. Choose 'IVF clinic or agency' and tell us your cohort size."],
  ["Do I need devices before I sign up?", "No. We'll help you choose a validated cuff and confirm compatibility."],
  ["Is the iPhone app available on the App Store?", "The iPhone app is currently in beta through Apple TestFlight. Share your Apple ID email with us and we'll send you an invite. The Android app is live on Google Play."],
  ["Does FutureFamilyAI replace my doctor or prenatal visits?", "No. FutureFamilyAI is a wellness tool that organizes your readings and symptoms so your own clinician has more context between visits. Your care team makes every medical decision, and you should keep all scheduled appointments."],
  ["How much does FutureFamilyAI cost?", "Eligible participants pay no subscription fee through December 31, 2026."],
];

const INITIAL = {
  role: "", name: "", email: "", phone: "", state: "", apple_id_email: "",
  stage: "", due_date: "", organization: "", job_role: "", cohort_size: "",
  source: "", consent: false,
};

const Path = ({ children }) => <span className="ffa-path">{children}</span>;

function DeviceSetup() {
  return (
    <div className="ffa-c-devices">
      <section className="ffa-app" id="download-app">
        {/* ===== SECTION 1: DOWNLOAD ===== */}
        <h2>Download the FutureFamilyAI App</h2>
        <p className="ffa-sub">Available on Android and iPhone. Track your health, monitor blood pressure and stay connected with your care team.</p>

        <div className="ffa-grid">
          <div className="ffa-card">
            <span className="ffa-tag live">Live on Google Play</span>
            <h3>Android</h3>
            <p>Download FutureFamilyAI directly from the Google Play Store and sign in to get started.</p>
            <a className="ffa-btn android" href="https://play.google.com/store/apps/details?id=com.app.futurefamily&pcampaignid=web_share" target="_blank" rel="noopener noreferrer">Get it on Google Play →</a>
          </div>

          <div className="ffa-card">
            <span className="ffa-tag beta">Beta · TestFlight</span>
            <h3>iPhone</h3>
            <p>The iPhone app is currently available through <strong>Apple TestFlight</strong>. Request access by sharing your Apple ID with us, then install the app using the steps below.</p>
            <a className="ffa-btn ios" href="#ffa-ios-steps">Request iPhone Access ↓</a>
          </div>
        </div>

        {/* ===== SECTION 2: iPHONE TESTFLIGHT ===== */}
        <div className="ffa-block" id="ffa-ios-steps">
          <h2>Get the iPhone App via TestFlight</h2>
          <p className="ffa-sub">Follow these two steps to receive and install the test version on your iPhone.</p>

          <details open>
            <summary>Step 1 — Find and share your Apple ID</summary>
            <div className="ffa-body">
              <p>Your Apple ID is the email address you use for the App Store and iCloud.</p>
              <h4>Find it on your iPhone</h4>
              <ol>
                <li>Open <strong>Settings</strong>.</li>
                <li>Tap your name at the very top.</li>
                <li>Your Apple ID email is shown just under your name.</li>
              </ol>
              <p>Or open the <strong>App Store</strong>, tap your profile picture (top right) and check the email shown.</p>
              <h4>Send it to us</h4>
              <p>Submit the <a href="#request-form">request form below</a> (choose your role and add your Apple ID email in the form), or call <a href="tel:+12126617673">(212) 661-7673</a>. Include:</p>
              <ul>
                <li>Your full name</li>
                <li>Your Apple ID email, exactly as shown in Settings</li>
              </ul>
              <div className="ffa-note"><strong>Only share your email address.</strong> Never share your Apple ID password or verification codes — nobody needs them to add you as a tester.</div>
              <p>Don&apos;t have an Apple ID? Create one in <Path>Settings → Sign in to your iPhone → Don&apos;t have an Apple ID or forgot it? → Create Apple ID</Path>.</p>
            </div>
          </details>

          <details>
            <summary>Step 2 — Install the app with TestFlight</summary>
            <div className="ffa-body">
              <p>Once we add you, Apple&apos;s TestFlight will email you an invite.</p>
              <ol>
                <li>On your iPhone, install <strong>TestFlight</strong> from the App Store (a free app from Apple).</li>
                <li>Open the invite on your iPhone:
                  <ul>
                    <li><strong>Email invite:</strong> tap <em>View in TestFlight</em>. If asked for a code, copy the redeem code from the email, then in TestFlight tap <em>Redeem</em> and paste it.</li>
                    <li><strong>Public link:</strong> open the link and tap <em>Accept</em>.</li>
                  </ul>
                </li>
                <li>Sign in to TestFlight with the Apple ID you shared.</li>
                <li>Tap <strong>Accept</strong>, then <strong>Install</strong>. For a newer build, tap <strong>Update</strong>.</li>
                <li>Open the app. An orange dot next to its name marks it as a beta build.</li>
              </ol>
            </div>
          </details>
        </div>

        {/* ===== SECTION 3: SMARTWATCH DATA SYNC ===== */}
        <div className="ffa-block" id="ffa-watch-sync">
          <h2>Sync Your Smartwatch Health Data</h2>
          <p className="ffa-sub">Send your watch data to your phone&apos;s built-in health app (Apple Health on iPhone, Health Connect on Android) so FutureFamilyAI can read it.</p>

          <ul className="ffa-chips">
            <li>Heart Rate</li><li>Respiratory Rate</li><li>Heart Rate Variability (HRV)</li><li>Skin Temperature</li><li>Movement (Steps &amp; Distance)</li><li>Active Energy</li>
          </ul>

          <h3>Supported watches</h3>
          <div className="ffa-hw">
            <div><b>iPhone</b><span>Apple Watch (wrist/skin temperature needs Series 8, Ultra or later)</span></div>
            <div><b>Android – Google</b><span>Google Pixel Watch 2 or later, Fitbit Air</span></div>
            <div><b>Android – Samsung</b><span>Samsung Galaxy Watch</span></div>
          </div>

          <details open>
            <summary>iPhone — Apple Watch → Apple Health</summary>
            <div className="ffa-body">
              <p>Apple Watch saves its data to Apple Health automatically once it&apos;s paired. You just need to turn on the right settings.</p>
              <h4>One-time setup</h4>
              <ol>
                <li>Pair your Apple Watch using the <strong>Watch</strong> app on your iPhone.</li>
                <li>On the iPhone, open <Path>Settings → Privacy &amp; Security → Motion &amp; Fitness</Path> and turn on <strong>Fitness Tracking</strong>.</li>
                <li>In the Watch app, open <Path>My Watch → Privacy</Path> and turn on <strong>Heart Rate</strong> (and <strong>Fitness Tracking</strong>, if shown).</li>
                <li>In the Watch app, open <Path>My Watch → Passcode</Path> and make sure <strong>Wrist Detection</strong> is on.</li>
                <li>Set up <strong>Sleep</strong> in the Health app (<Path>Browse → Sleep → Get Started</Path>) and wear your watch to bed. Respiratory rate and wrist temperature are recorded during sleep. Wrist temperature shows after about 5 nights.</li>
              </ol>
              <h4>Where each metric appears in Apple Health</h4>
              <ul>
                <li><strong>Heart Rate, HRV:</strong> <Path>Browse → Heart</Path></li>
                <li><strong>Respiratory Rate:</strong> <Path>Browse → Respiratory</Path></li>
                <li><strong>Wrist (Skin) Temperature:</strong> <Path>Browse → Body Measurements</Path></li>
                <li><strong>Steps, Distance, Active Energy:</strong> <Path>Browse → Activity</Path></li>
              </ul>
              <h4>Allow FutureFamilyAI to read your data</h4>
              <ol>
                <li>When FutureFamilyAI asks for Health access, turn on Heart Rate, Heart Rate Variability, Respiratory Rate, Wrist Temperature, Steps, Walking + Running Distance and Active Energy, then tap <strong>Allow</strong>.</li>
                <li>To change this later: <Path>Health app → profile picture → Apps → FutureFamilyAI</Path> → turn categories on or off.</li>
              </ol>
            </div>
          </details>

          <details>
            <summary>Android — Google Pixel Watch 2+ / Fitbit Air → Health Connect</summary>
            <div className="ffa-body">
              <ol>
                <li>Pair your watch using the <strong>Google Pixel Watch</strong> app (Pixel Watch) or the <strong>Fitbit</strong> app (Fitbit Air), and sign in to your Fitbit / Google account.</li>
                <li>Make sure <strong>Health Connect</strong> is available. It&apos;s built into Android 14 and later (<Path>Settings → Security &amp; privacy → Privacy → Health Connect</Path>). On Android 13, install Health Connect from Google Play.</li>
                <li>In the Fitbit app, tap your profile picture, open <strong>Health Connect</strong>, and turn on sync.</li>
                <li>Allow Fitbit to write Heart Rate, Heart Rate Variability, Respiratory Rate, Skin Temperature, Steps, Distance and Active Calories.</li>
                <li>Wear your watch to sleep. Respiratory rate, HRV and skin temperature are measured overnight.</li>
              </ol>
            </div>
          </details>

          <details>
            <summary>Android — Samsung Galaxy Watch → Health Connect</summary>
            <div className="ffa-body">
              <ol>
                <li>Pair your watch using the <strong>Galaxy Wearable</strong> app and sign in to <strong>Samsung Health</strong>.</li>
                <li>Make sure <strong>Health Connect</strong> is available (built into Android 14 and later; install from Google Play on Android 13).</li>
                <li>In Samsung Health, open <Path>Menu (⋮) → Settings → Health Connect</Path> and allow Samsung Health to share Heart Rate, Heart Rate Variability, Respiratory Rate, Skin Temperature, Steps, Distance and Active Calories.</li>
                <li>In the Galaxy Wearable app, open <Path>Watch settings → Samsung Health</Path> and set heart rate measurement to <strong>Continuous</strong> (or as often as possible).</li>
                <li>Wear your watch to sleep. Respiratory rate and skin temperature are measured overnight.</li>
              </ol>
            </div>
          </details>

          <details>
            <summary>Android — Allow FutureFamilyAI to read Health Connect</summary>
            <div className="ffa-body">
              <ol>
                <li>When FutureFamilyAI asks for Health Connect access, turn on Heart Rate, Heart Rate Variability, Respiratory Rate, Skin Temperature, Steps, Distance, Active Calories and Blood Pressure, then tap <strong>Allow</strong>.</li>
                <li>To change this later: open <strong>Health Connect</strong> → <Path>App permissions → FutureFamilyAI</Path> → turn categories on or off.</li>
              </ol>
            </div>
          </details>

          <div className="ffa-note">Available metrics depend on your watch model and app version. Some metrics, like skin temperature, respiratory rate and HRV, are only recorded while you sleep.</div>
        </div>

        {/* ===== SECTION 4: BP MONITORING ===== */}
        <div className="ffa-block" id="ffa-bp-setup">
          <h2>Blood Pressure Monitoring Setup</h2>
          <p className="ffa-sub">BP monitoring works on both Android and iPhone. Here&apos;s the hardware you&apos;ll need and how to connect it.</p>

          <h3>Hardware requirements</h3>
          <div className="ffa-hw">
            <div><b>iPhone users</b><span>iPhone + Apple Watch</span></div>
            <div><b>Android users</b><span>Android phone + one of: Google Pixel Watch 2 or later, Fitbit Air, or Samsung Galaxy Watch</span></div>
            <div><b>BP monitor (both)</b><span>OMRON upper-arm cuff with Bluetooth</span></div>
          </div>

          <details open>
            <summary>1. Pair your OMRON monitor with the OMRON connect app</summary>
            <div className="ffa-body">
              <h4>One-time setup</h4>
              <ol>
                <li>Install <strong>OMRON connect</strong> from the App Store or Google Play and create an OMRON account, or sign in.</li>
                <li>Turn on Bluetooth on your phone.</li>
                <li>In the app, go to <Path>Profile / Menu → My Devices → Add Device</Path> (or &quot;+&quot;).</li>
                <li>Pick your monitor model from the list, or scan the QR code on the box.</li>
                <li>Put the monitor into pairing mode. On most models, hold the Bluetooth / transfer button (or START) for a few seconds until &quot;P&quot; or a flashing Bluetooth icon appears. Check your model&apos;s manual for the exact button.</li>
                <li>Keep the phone close to the monitor. When the app shows the device, tap <strong>Pair</strong>. Accept the Bluetooth pairing request if it appears.</li>
                <li>The monitor shows &quot;OK&quot; or &quot;End&quot; when pairing is done.</li>
              </ol>
              <h4>Each time you measure</h4>
              <ol>
                <li>Take a reading as usual.</li>
                <li>Open OMRON connect. On most models, readings sync automatically over Bluetooth; on some, press the Bluetooth/transfer button or pull down to refresh.</li>
                <li>Check that the reading appears on the Dashboard / History screen.</li>
              </ol>
            </div>
          </details>

          <details>
            <summary>2. iPhone — Sync OMRON connect with Apple Health</summary>
            <div className="ffa-body">
              <ol>
                <li>Open <strong>OMRON connect</strong> and tap the <strong>Contents</strong> tab at the bottom of the screen.</li>
                <li>Tap <strong>+ Add contents</strong> to open the <strong>Contents Library</strong>.</li>
                <li>Scroll down to <strong>Apple Health Data Sharing</strong> (&quot;Link to Healthcare app and use your data with other third-party apps&quot;).</li>
                <li>Tap <strong>Share setting</strong>.</li>
                <li>On the Health permission screen, turn on the categories you want, typically:
                  <ul>
                    <li>Blood Pressure (Systolic / Diastolic)</li>
                    <li>Heart Rate</li>
                    <li>Weight / Body Fat / BMI, if you have an OMRON scale</li>
                  </ul>
                </li>
                <li>Tap <strong>Allow</strong>.</li>
                <li>Take a new reading, or reopen the app. New readings appear in the Health app under <Path>Browse → Heart → Blood Pressure</Path>.</li>
              </ol>
              <p><strong>To check or change permissions later:</strong> <Path>Health app → profile picture → Apps (or Privacy → Apps) → OMRON connect</Path> → turn categories on or off.</p>
            </div>
          </details>

          <details>
            <summary>3. Android — Sync OMRON connect with Health Connect</summary>
            <div className="ffa-body">
              <ol>
                <li>In OMRON connect, open <Path>Profile / Menu → App Settings (or Connected Apps) → Health Connect</Path>.</li>
                <li>Turn on sync and allow access to Blood Pressure and Heart Rate.</li>
                <li>Take a new reading, or reopen the app, so new readings are shared with FutureFamilyAI.</li>
              </ol>
            </div>
          </details>
        </div>
      </section>
    </div>
  );
}

export default function ContactFutureFamilyAI() {
  const [values, setValues] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  const group = ROLES.find((r) => r.value === values.role)?.group ?? null;

  const setField = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => ({ ...e, [name]: false }));
  };

  const onChange = (e) => {
    const { name, type, checked, value } = e.target;
    setField(name, type === "checkbox" ? checked : value);
  };

  // Hero buttons preselect a role type (only if none chosen yet)
  const preselect = (kind) => {
    if (values.role) return;
    setField("role", kind === "provider" ? "Physician/OB" : "Expecting/planning mother");
  };

  const validate = () => {
    const err = {
      role: !values.role,
      name: values.name.trim().length <= 1,
      email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim()),
      state: !values.state,
      consent: !values.consent,
    };
    if (group === "member") err.stage = !values.stage;
    if (group === "provider") {
      err.organization = values.organization.trim().length <= 1;
      err.job_role = values.job_role.trim().length <= 1;
    }
    return err;
  };

  const buildPayload = () => {
    const payload = {
      role: values.role,
      group,
      name: values.name.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      state: values.state,
      apple_id_email: values.apple_id_email.trim(),
      source: values.source,
      consent: values.consent,
    };
    if (group === "member") {
      payload.stage = values.stage;
      payload.due_date = values.due_date;
    }
    if (group === "provider") {
      payload.organization = values.organization.trim();
      payload.job_role = values.job_role.trim();
      payload.cohort_size = values.cohort_size.trim();
    }
    return payload;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const err = validate();
    setErrors(err);
    const firstKey = Object.keys(err).find((k) => err[k]);
    if (firstKey) {
      const el = document.querySelector(`[data-field="${firstKey}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    setServerError("");
    try {
      await submitContactRequest(buildPayload());
      setSubmitted(true);
    } catch (ex) {
      setServerError(ex.message || "Something went wrong. Please try again or call (212) 661-7673.");
    } finally {
      setSubmitting(false);
    }
  };

  const fc = (name, extra = "") => `ffa-c-field${extra ? " " + extra : ""}${errors[name] ? " ffa-c-invalid" : ""}`;

  return (
    <div className="ffa-contact">
      <Header />

      <main>
        <div className="ffa-c-wrap">
          <nav className="ffa-c-crumbs" aria-label="Breadcrumb"><a href="/">Home</a> › Contact / Request Demo</nav>

          <aside className="ffa-c-facts">
            <h2>Fast facts</h2>
            <p>Tell us whether you are an expecting or planning mother, a surrogate, a physician or a referring clinic or agency, and our team will help you join the free pilot (through December 31, 2026) or schedule a demo.</p>
            <details>
              <summary>More fast facts: audience, key stat, safety, cost</summary>
              <p>FutureFamilyAI is a physician-connected pregnancy and family-planning wellness app that turns home blood pressure readings from a validated cuff, smartwatch wellness trends and symptom notes into a simple color status your care team can review between visits. Its monitoring protocol was developed by Dr. Kulsoom Baloch, MBBS, MPH, using ACOG, SMFM, ISSHP and AHA guidance, and every page is medically reviewed by Dr. Rashmi Gulati, MD, Medical Director of Patients Medical (Manhattan, since 1974). FutureFamilyAI is a product of HelixFoundry, Inc., and is used by Surrogacy4All to support its gestational surrogates.</p>
            </details>
          </aside>

          <section className="ffa-c-hero">
            <div>
              <p className="ffa-c-eyebrow">Let&apos;s get you started</p>
              <h1>Request Free Pilot Access or a Clinical Demo</h1>
              <p className="ffa-c-lead">Tell us whether you are an expecting or planning mother, a surrogate, a physician or a referring clinic or agency, and our team will help you join the free pilot (through December 31, 2026) or schedule a demo. Most requests receive a reply within one business day.</p>
              <p className="ffa-c-warn-inline">Please don&apos;t send urgent medical questions through this form — call your provider or 911.</p>
              <p className="ffa-c-byline">
                Written by <strong>Dr. Kulsoom Baloch, MBBS, MPH</strong><br />
                Medically reviewed by <strong>Dr. Rashmi Gulati, MD</strong><br />
                Published October 2026 · Last updated October 2026
              </p>
              <div className="ffa-c-cta-row">
                <a className="ffa-c-btn ffa-c-btn-primary" href="#request-form" onClick={() => preselect("member")}>Request Free Pilot Access</a>
                <a className="ffa-c-btn ffa-c-btn-outline" href="#request-form" onClick={() => preselect("provider")}>Request a Clinical Demo</a>
              </div>
            </div>
            {/* Replace with photo: care coordinator with headset at a laptop in a bright office */}
            <img
              className="ffa-c-photo-img"
              src="/images/contact-us.png"
              alt="FutureFamilyAI team member ready to help families and clinicians get started"
            />
          </section>
        </div>

        <DeviceSetup />

        {/* ===== REQUEST FORM ===== */}
        <section className="ffa-c-form-sec" id="request-form">
          <div className="ffa-c-wrap ffa-c-form-grid">
            <div className="ffa-c-card">
              {!submitted && (
                <form id="ffa-form" noValidate onSubmit={onSubmit}>
                  <h2>Tell us about you</h2>
                  <p className="ffa-c-sub">Fields marked <span className="ffa-c-req">*</span> are required.</p>

                  <fieldset className={fc("role")} data-field="role">
                    <legend>I am a <span className="ffa-c-req">*</span></legend>
                    <div className="ffa-c-roles">
                      {ROLES.map((r) => (
                        <label className="ffa-c-role" key={r.value}>
                          <input
                            type="radio"
                            name="role"
                            value={r.value}
                            checked={values.role === r.value}
                            onChange={onChange}
                          />
                          <span>{r.label}<small>{r.small}</small></span>
                        </label>
                      ))}
                    </div>
                    <p className="ffa-c-err-msg">Please choose the option that describes you.</p>
                  </fieldset>

                  <div className="ffa-c-row">
                    <div className={fc("name")} data-field="name">
                      <label className="ffa-c-lbl" htmlFor="name">Full name <span className="ffa-c-req">*</span></label>
                      <input type="text" id="name" name="name" autoComplete="name" value={values.name} onChange={onChange} />
                      <p className="ffa-c-err-msg">Please enter your name.</p>
                    </div>
                    <div className={fc("email")} data-field="email">
                      <label className="ffa-c-lbl" htmlFor="email">Email <span className="ffa-c-req">*</span></label>
                      <input type="email" id="email" name="email" autoComplete="email" value={values.email} onChange={onChange} />
                      <p className="ffa-c-err-msg">Please enter a valid email address.</p>
                    </div>
                  </div>

                  <div className="ffa-c-row">
                    <div className="ffa-c-field">
                      <label className="ffa-c-lbl" htmlFor="phone">Phone <span className="ffa-c-opt">(optional)</span></label>
                      <input type="tel" id="phone" name="phone" autoComplete="tel" placeholder="(555) 555-5555" value={values.phone} onChange={onChange} />
                    </div>
                    <div className={fc("state")} data-field="state">
                      <label className="ffa-c-lbl" htmlFor="state">State <span className="ffa-c-req">*</span></label>
                      <select id="state" name="state" value={values.state} onChange={onChange}>
                        <option value="">Select your state</option>
                        {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <p className="ffa-c-err-msg">Please select your state.</p>
                    </div>
                  </div>

                  <div className="ffa-c-field">
                    <label className="ffa-c-lbl" htmlFor="appleid">Apple ID email <span className="ffa-c-opt">(optional — for iPhone beta access)</span></label>
                    <input type="email" id="appleid" name="apple_id_email" autoComplete="off" value={values.apple_id_email} onChange={onChange} />
                    <p className="ffa-c-hint">Only if you use an iPhone. We&apos;ll send you a TestFlight invite. Never share your Apple ID password.</p>
                  </div>

                  {/* Members */}
                  <div className={`ffa-c-cond${group === "member" ? " ffa-c-show" : ""}`} id="cond-member">
                    <div className="ffa-c-row">
                      <div className={fc("stage")} data-field="stage">
                        <label className="ffa-c-lbl" htmlFor="stage">Current stage <span className="ffa-c-req">*</span></label>
                        <select id="stage" name="stage" value={values.stage} onChange={onChange}>
                          <option value="">Select stage</option>
                          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <p className="ffa-c-err-msg">Please select your stage.</p>
                      </div>
                      <div className="ffa-c-field">
                        <label className="ffa-c-lbl" htmlFor="due">Due date <span className="ffa-c-opt">(if pregnant)</span></label>
                        <input type="date" id="due" name="due_date" value={values.due_date} onChange={onChange} />
                      </div>
                    </div>
                  </div>

                  {/* Providers */}
                  <div className={`ffa-c-cond${group === "provider" ? " ffa-c-show" : ""}`} id="cond-provider">
                    <div className="ffa-c-row">
                      <div className={fc("organization")} data-field="organization">
                        <label className="ffa-c-lbl" htmlFor="org">Organization <span className="ffa-c-req">*</span></label>
                        <input type="text" id="org" name="organization" autoComplete="organization" value={values.organization} onChange={onChange} />
                        <p className="ffa-c-err-msg">Please enter your organization.</p>
                      </div>
                      <div className={fc("job_role")} data-field="job_role">
                        <label className="ffa-c-lbl" htmlFor="jobrole">Your role <span className="ffa-c-req">*</span></label>
                        <input type="text" id="jobrole" name="job_role" placeholder="e.g. OB/GYN, Clinic Director" autoComplete="organization-title" value={values.job_role} onChange={onChange} />
                        <p className="ffa-c-err-msg">Please enter your role.</p>
                      </div>
                    </div>
                    <div className="ffa-c-field" style={{ marginTop: 18 }}>
                      <label className="ffa-c-lbl" htmlFor="cohort">Estimated cohort size <span className="ffa-c-opt">(for group pilots)</span></label>
                      <input type="text" id="cohort" name="cohort_size" placeholder="e.g. 25 patients" value={values.cohort_size} onChange={onChange} />
                    </div>
                  </div>

                  <div className="ffa-c-field">
                    <label className="ffa-c-lbl" htmlFor="source">How did you hear about us?</label>
                    <select id="source" name="source" value={values.source} onChange={onChange}>
                      <option value="">Select one</option>
                      {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <div className={fc("consent")} data-field="consent">
                    <label className="ffa-c-consent">
                      <input type="checkbox" id="consent" name="consent" checked={values.consent} onChange={onChange} />
                      <span>Yes, contact me about FutureFamilyAI. <span className="ffa-c-req">*</span></span>
                    </label>
                    <p className="ffa-c-err-msg" style={{ marginTop: -12, marginBottom: 12 }}>Please check this box so we can contact you.</p>
                  </div>

                  {serverError && (
                    <p role="alert" style={{ color: "var(--err)", fontSize: 14, margin: "0 0 12px" }}>{serverError}</p>
                  )}
                  <button type="submit" disabled={submitting} className="ffa-c-btn ffa-c-btn-primary" style={{ width: "100%", fontSize: 16, padding: 15, opacity: submitting ? 0.7 : 1 }}>
                    {submitting ? "Sending…" : "Send my request"}
                  </button>
                  <p className="ffa-c-form-note">Please don&apos;t send urgent medical questions through this form. Contact your provider or call 911.</p>
                </form>
              )}

              <div className={`ffa-c-success${submitted ? " ffa-c-show" : ""}`} role="status" aria-live="polite">
                <div className="ffa-c-tick">✓</div>
                <h3>Thanks — we&apos;ve got your request</h3>
                <p style={{ color: "var(--muted)", margin: "0 0 18px" }}>A member of the FutureFamilyAI team matched to your role will reply within one business day. While you wait, you can download the app above.</p>
                <a className="ffa-c-btn ffa-c-btn-outline" href="#download-app">Download the app ↑</a>
              </div>
            </div>

            <div className="ffa-c-aside">
              <div className="ffa-c-card">
                <h3>Prefer to talk?</h3>
                <p>Call during business hours.</p>
                <a className="ffa-c-phone" href="tel:+12126617673">(212) 661-7673</a>
              </div>
              <div className="ffa-c-card">
                <span className="ffa-c-pill">Free pilot</span>
                <h3>Join the free pilot</h3>
                <p>No subscription fee through Dec 31, 2026. Free pilot enrollment is open through December 31, 2026.</p>
              </div>
              <div className="ffa-c-card">
                <h3>What you&apos;ll need</h3>
                <ul>
                  <li>iPhone + Apple Watch, or Android phone + Pixel Watch 2+, Fitbit Air or Galaxy Watch</li>
                  <li>OMRON upper-arm Bluetooth BP cuff</li>
                </ul>
                <p style={{ marginTop: 10 }}>No devices yet? That&apos;s fine — we&apos;ll help you choose. <a href="#download-app">Setup guides ↑</a></p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className="ffa-c-faq">
          <div className="ffa-c-wrap">
            <h2 className="ffa-c-center">Frequently asked questions</h2>
            <div className="ffa-c-faq-list">
              {FAQS.map(([q, a, open]) => (
                <details key={q} open={open || undefined}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <div className="ffa-c-wrap">
          <div className="ffa-c-author">
            <div className="ffa-c-avatar">KB</div>
            <div>
              <h3>Dr. Kulsoom Baloch, MBBS, MPH</h3>
              <p>Clinical Protocol Author, FutureFamilyAI. Dr. Kulsoom Baloch, MBBS, MPH, is the clinical protocol author for FutureFamilyAI and a published researcher in reproductive and maternal health. She developed the app&apos;s monitoring protocol using ACOG, SMFM, ISSHP and AHA guidance.</p>
              <p style={{ fontSize: 13 }}>Medically reviewed by Dr. Rashmi Gulati, MD, Medical Director, Patients Medical. Last updated: October 2026.</p>
            </div>
          </div>

          <div className="ffa-c-keep">
            <h3>Keep reading</h3>
            <div className="ffa-c-keep-links">
              {KEEP.map(([href, label]) => <a key={href} href={href}>{label}</a>)}
            </div>
          </div>

          <p className="ffa-c-disclaimer">FutureFamilyAI is a wellness and awareness tool. It is not a medical device, is not FDA-cleared, does not diagnose or treat any condition, and is not monitored in real time. It supports — and does not replace — your prenatal care. In an emergency, call 911.</p>

          <div className="ffa-c-final-cta">
            <div>
              <h2>Join the free pilot</h2>
              <p>No subscription fee through Dec 31, 2026.</p>
            </div>
            <a className="ffa-c-btn ffa-c-btn-primary" href="#request-form">Request Free Pilot Access</a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}